import type { Price, TogethernessMode, User } from './types'

/** Honest price formatting — integer cents + currency, never floats in app logic. */
export function formatPrice(price: Price | null | undefined): string {
  if (!price) return ''
  if (price.cents === 0) return 'Free'
  const major = price.cents / 100
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: price.currency || 'USD',
    }).format(major)
  } catch {
    return `${(price.currency || 'USD')} ${major.toFixed(2)}`
  }
}

/**
 * Signed wallet-ledger amount: "+$10.00" credit / "−$4.99" debit (true minus,
 * U+2212). Reuses formatPrice; ledger entries are never zero cents, so the
 * price-flavored 0 → "Free" case is unreachable here.
 */
export function formatSignedAmount(cents: number, currency: string): string {
  const sign = cents < 0 ? '−' : '+'
  return `${sign}${formatPrice({ cents: Math.abs(cents), currency })}`
}

/** The full togetherness spectrum (product-decisions): single → party. */
export const TOGETHERNESS_MODES: {
  value: TogethernessMode
  label: string
  hint: string
}[] = [
  { value: 'single_player', label: 'Solo', hint: 'Each on their own' },
  { value: 'joinable_parallel', label: 'Joinable', hint: 'Alone together' },
  { value: 'true_coop', label: 'Co-op', hint: 'Truly together' },
  { value: 'competitive', label: 'Competitive', hint: 'Against each other' },
  { value: 'party', label: 'Party', hint: 'Loud and silly' },
]

export function togethernessLabel(mode: TogethernessMode | null | undefined): string {
  return TOGETHERNESS_MODES.find((m) => m.value === mode)?.label ?? '—'
}

export function avatarInitials(name: string | null | undefined): string {
  if (!name) return '??'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function steamStoreUrl(appid: number): string {
  return `https://store.steampowered.com/app/${appid}`
}

/**
 * Playtime as a neutral, factual read ("142h played" / "Never played") — a STAT,
 * never a ranking or preference score (playtime is confidence, not preference;
 * skill guardrail #1). Minutes in, coarse hours out.
 */
export function formatPlaytime(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return 'Never played'
  const hours = minutes / 60
  if (hours < 1) return '< 1h played'
  return `${Math.round(hours)}h played`
}

/** Calm, locale-aware date for profile metadata (member-since, last synced). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date)
}

/** The signed-in user's own library-sync lifecycle, derived from /me. */
export type LibrarySyncState = 'never_synced' | 'syncing' | 'synced_empty' | 'synced'

/**
 * Derive the honest Steam-library sync state for the signed-in user from the
 * /me payload — every input is a server fact; the client invents no sync
 * logic. Evidence that a sync completed is EITHER flag (`library_readable` or
 * a `last_synced_at` timestamp), so the read survives both payload shapes for
 * a completed-but-empty sync.
 *   never_synced — consent never given: the sync has never been allowed to run
 *   syncing      — consent is on but no sync evidence yet (job still pending —
 *                  or Steam couldn't read the library; the payload can't tell)
 *   synced_empty — a sync completed and found zero games
 *   synced       — a sync completed and found games
 */
export function librarySyncState(
  user:
    | Pick<User, 'recommendation_consent' | 'library_readable' | 'last_synced_at' | 'owned_game_count'>
    | null
    | undefined,
): LibrarySyncState {
  if (!user) return 'never_synced'
  const syncEvidence = user.library_readable || Boolean(user.last_synced_at)
  if (!syncEvidence) return user.recommendation_consent ? 'syncing' : 'never_synced'
  return user.owned_game_count > 0 ? 'synced' : 'synced_empty'
}

/** The Badge variants this app ships (mirrors components/ui/Badge.tsx). */
export type LibraryBadge = {
  label: string
  variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger'
}

/**
 * Map a member's Steam-library readiness to a Badge (label + variant). Pure and
 * data-driven off the backend's library_status enum — the client invents no
 * readiness logic. Returns null for unknown/unset status so the caller renders
 * nothing rather than a misleading chip.
 *   synced → "N games" (info) · empty → "no games yet" (neutral)
 *   private → "profile private" (warning) · unsynced → "not synced" (warning)
 */
export function libraryBadge(
  status: string | null | undefined,
  ownedGameCount?: number | null,
): LibraryBadge | null {
  switch (status) {
    case 'synced': {
      const n = ownedGameCount ?? 0
      return { label: `${n} ${n === 1 ? 'game' : 'games'}`, variant: 'info' }
    }
    case 'empty':
      return { label: 'no games yet', variant: 'neutral' }
    case 'private':
      return { label: 'profile private', variant: 'warning' }
    case 'unsynced':
      return { label: 'not synced', variant: 'warning' }
    default:
      return null
  }
}
